import {css, StyleSheet} from 'aphrodite';
import reactUpdate from 'immutability-helper';
import React from 'react';

import {assert} from 'Asserts';
import {SchemalessBulkImportSupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {BASE_FONT_PROXIMA, FONT_PROXIMA_13} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';

import ColumnTypeDropdown from '../ColumnTypeDropdown';
import {
  COLUMN_TYPE,
  ColumnTransformConfiguration,
  DerivedColumn,
  IMPORT_TYPE,
  SelectedSchemaIds,
  SpreadsheetColumn,
} from '../SpreadsheetBulkImportConstants';
import {getFieldName} from '../SpreadsheetBulkImportUtil';

export interface DerivedColumnsTableProps {
  columns: Array<SpreadsheetColumn>;
  columnTransformConfigs: Array<ColumnTransformConfiguration>;
  derivedColumns: Array<DerivedColumn>;
  getValidSchemaFieldIds: (columnIndex: number) => Array<string>;
  importType: IMPORT_TYPE;
  onUpdateDerivedColumns: (derivedColumns: Array<DerivedColumn>) => void;
  selectedSchemaIds: SelectedSchemaIds;
  schemalessCreationEntityType: SchemalessBulkImportSupportedOligoAwareTypes;
  tableColumnNames: Array<string>;
}

// Finds the column name(s) corresponding to the column(s) with type in `columnTypes`.
function findColumnNames(columns: Array<SpreadsheetColumn>, columnTypes: Array<COLUMN_TYPE>): string {
  const matchingColumns = columns.filter((column) => columnTypes.includes(column.type));
  return matchingColumns.map((column) => column.name).join(',');
}

/**
 * This component is analogous to SpreadsheetColumnsTable in that it allows the user to map derived columns to
 * the desired column type. Additional information is provided relative to that table since derived columns
 * are not bonafide columns that exist in the spreadsheet itself.
 */
export default function DerivedColumnsTable({
  columnTransformConfigs,
  columns,
  derivedColumns,
  getValidSchemaFieldIds,
  importType,
  onUpdateDerivedColumns,
  schemalessCreationEntityType,
  selectedSchemaIds,
  tableColumnNames,
}: DerivedColumnsTableProps): JSX.Element {
  return (
    <table className="table">
      <thead>
        <tr className={css(styles.text)}>
          <th>Column Name</th>
          <th>Sample Data</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {derivedColumns.map((derivedColumn, i) => {
          const matchingConfigs = columnTransformConfigs.filter(
            (config) => config.columnTransform === derivedColumn.columnTransform
          );
          assert(matchingConfigs.length === 1, 'Expected to find only one matching column transform config.');
          const SampleDataRenderer = matchingConfigs[0].sampleDataRenderer;
          return (
            <tr key={i}>
              <td className={css(styles.nameColumn)}>
                <span className={css(styles.text)}>
                  {findColumnNames(
                    columns,
                    Object.keys(derivedColumn.exampleValues[0].originalValues) as Array<COLUMN_TYPE>
                  )}
                </span>
              </td>
              <td className="bulk-import--column-text-column">
                <SampleDataRenderer
                  derivedValue={derivedColumn.exampleValues[0].value}
                  originalValues={derivedColumn.exampleValues[0].originalValues}
                  columnTransform={derivedColumn.columnTransform}
                />
                <span className={css(styles.similarRowsText)}>{`${(
                  derivedColumn.numRowsWithDerivedValues - 1
                ).toString()} of your other rows ${
                  derivedColumn.numRowsWithDerivedValues - 1 === 1 ? 'has' : 'have'
                } similar data.`}</span>
              </td>
              <td className="bulk-import--column-type-column">
                <ColumnTypeDropdown
                  className={null}
                  column={derivedColumn}
                  columnIdx={i}
                  columns={derivedColumns}
                  columnTypes={derivedColumn.allowedColumnTypes}
                  dataTableKeyFields={[]}
                  getMenuItemName={(columnType) =>
                    getFieldName(columnType, selectedSchemaIds, importType, schemalessCreationEntityType)
                  }
                  getValidSchemaFieldIds={getValidSchemaFieldIds}
                  importType={importType}
                  onChangeColumn={(
                    columnIndex,
                    newColumnType,
                    {newSchemaField, newTableColumnName, newSampleType} = {}
                  ) => {
                    const newColumns = reactUpdate(derivedColumns, {
                      [columnIndex]: {
                        type: {$set: newColumnType},
                        schemaField: {$set: newSchemaField},
                        tableColumnName: {$set: newTableColumnName},
                        sampleType: {$set: newSampleType},
                      },
                    });
                    onUpdateDerivedColumns(newColumns);
                  }}
                  selectedSchemaIds={selectedSchemaIds}
                  tableColumnNames={tableColumnNames}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const styles = StyleSheet.create({
  nameColumn: {width: '100px'},
  text: {color: GRAY_DARKER, FONT_PROXIMA_13},
  similarRowsText: {
    color: GRAY_DARKER,
    ...BASE_FONT_PROXIMA,
    fontSize: 11,
  },
});
